/**
 * API Client with error handling and authentication
 * Provides a fetch wrapper with automatic auth headers, error handling, and token refresh
 */

import { env } from './env';
import { getMessageForErrorCode } from './error-codes';

// ============================================================================
// Custom Error Classes
// ============================================================================

/**
 * Base API error class
 */
export class ApiError extends Error {
  statusCode: number;
  errors?: Record<string, string[]>;
  code?: string;

  constructor(
    statusCode: number,
    message: string,
    errors?: Record<string, string[]>,
    code?: string
  ) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.errors = errors;
    this.code = code;
  }
}

/**
 * Validation error for form/input validation failures
 */
export class ValidationError extends Error {
  field: string;
  errors?: Record<string, string[]>;

  constructor(field: string, message: string, errors?: Record<string, string[]>) {
    super(message);
    this.name = 'ValidationError';
    this.field = field;
    this.errors = errors;
  }
}

/**
 * Authentication error for 401 responses
 */
export class AuthenticationError extends Error {
  code?: string;

  constructor(message: string = 'Authentication required', code?: string) {
    super(message);
    this.name = 'AuthenticationError';
    this.code = code;
  }
}

// ============================================================================
// Token Management
// ============================================================================

let authToken: string | null = null;
let refreshToken: string | null = null;
let tokenRefreshPromise: Promise<string> | null = null;
let authGeneration = 0;

/**
 * Set the authentication token
 */
export function setAuthToken(token: string | null) {
  if (authToken !== token) {
    authGeneration += 1;
  }
  authToken = token;
}

/**
 * Get the current authentication token
 */
export function getAuthToken(): string | null {
  return authToken;
}

/**
 * Set the refresh token
 */
export function setRefreshToken(token: string | null) {
  if (refreshToken !== token) {
    authGeneration += 1;
  }
  refreshToken = token;
}

/**
 * Get the current refresh token
 */
export function getRefreshToken(): string | null {
  return refreshToken;
}

/**
 * Clear all tokens
 */
export function clearTokens() {
  const didAuthStateChange = authToken !== null || refreshToken !== null;
  authToken = null;
  refreshToken = null;
  tokenRefreshPromise = null;

  if (didAuthStateChange) {
    authGeneration += 1;
  }
}

/**
 * Refresh the authentication token
 * Returns the new token on success, throws on failure
 * Only clears tokens on definitive auth failures (401/403), not network errors
 */
async function refreshAuthToken(): Promise<string> {
  // If a refresh is already in progress, return that promise
  if (tokenRefreshPromise) {
    return tokenRefreshPromise;
  }

  tokenRefreshPromise = (async () => {
    try {
      if (!refreshToken) {
        throw new AuthenticationError('No refresh token available');
      }

      const refreshTokenAtRequestStart = refreshToken;
      const authGenerationAtRequestStart = authGeneration;

      const response = await fetch(`${env.apiBaseUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: refreshTokenAtRequestStart }),
      });

      // Definitive auth failure - clear tokens
      if (response.status === 401 || response.status === 403) {
        clearTokens();
        // Also clear the Zustand store
        try {
          const { useAuthStore } = await import('../store/auth-store');
          useAuthStore.getState().logout();
        } catch {
          // Store not available
        }
        throw new AuthenticationError('Token refresh failed - session expired');
      }

      if (!response.ok) {
        // Non-auth error (network, server error) - don't clear tokens
        throw new Error(`Token refresh failed with status ${response.status}`);
      }

      const data = await response.json();
      const newToken = data.data?.accessToken || data.accessToken;
      const newRefreshToken = data.data?.refreshToken || data.refreshToken || refreshToken;

      if (!newToken) {
        throw new AuthenticationError('No token in refresh response');
      }

      if (authGeneration !== authGenerationAtRequestStart) {
        throw new AuthenticationError('Authentication state changed during token refresh');
      }

      setAuthToken(newToken);
      setRefreshToken(newRefreshToken || newToken);

      // Keep Zustand store in sync so it persists across reloads
      try {
        const { useAuthStore } = await import('../store/auth-store');
        useAuthStore.getState().setToken(newToken);
        useAuthStore.getState().setRefreshToken(newRefreshToken || newToken);
      } catch {
        // If store isn't available (e.g., during tests), skip the sync
      }

      return newToken;
    } finally {
      tokenRefreshPromise = null;
    }
  })();

  return tokenRefreshPromise;
}

// ============================================================================
// API Client
// ============================================================================

export interface ApiRequestOptions extends RequestInit {
  requiresAuth?: boolean;
  skipAuthRefresh?: boolean;
  // Optional requests may skip token refresh and logout while still throwing errors.
  skipAuthRecovery?: boolean;
}

interface ParsedErrorResponse {
  rawError: Record<string, unknown>;
  fallbackText: string;
}

interface NormalizedApiError {
  code: string;
  message: string;
  errors?: Record<string, string[]>;
  validationField?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asNonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function buildFallbackErrorMessage(response: Response, fallbackText: string): string {
  if (response.status >= 500) {
    return getMessageForErrorCode('INTERNAL_SERVER_ERROR');
  }
  const fallback = (fallbackText || response.statusText || '').trim();
  if (!fallback || fallback.startsWith('<')) {
    return `Request failed (${response.status})`;
  }
  return fallback;
}

async function parseJsonErrorResponse(
  response: Response,
  contentType: string
): Promise<Record<string, unknown>> {
  let parsed: unknown = {};
  try {
    parsed = await response.json();
  } catch (parseError) {
    if (env.features.enableDebugLogging) {
      console.warn('Failed to parse JSON error response body', {
        status: response.status,
        contentType,
        parseError,
      });
    }
  }
  return isRecord(parsed) ? parsed : {};
}

async function parseTextBodyErrorResponse(
  response: Response,
  contentType: string
): Promise<ParsedErrorResponse> {
  let textBody = '';
  try {
    textBody = (await response.text()).trim();
  } catch (readError) {
    if (env.features.enableDebugLogging) {
      console.warn('Failed to read error response text body', {
        status: response.status,
        contentType,
        readError,
      });
    }
  }

  if (!textBody) {
    return { rawError: {}, fallbackText: '' };
  }

  const maybeJsonText = textBody.startsWith('{') || textBody.startsWith('[');
  if (!maybeJsonText) {
    return {
      rawError: {},
      fallbackText: textBody,
    };
  }

  try {
    const parsed = JSON.parse(textBody);
    return {
      rawError: isRecord(parsed) ? parsed : {},
      fallbackText: textBody,
    };
  } catch (parseError) {
    if (env.features.enableDebugLogging) {
      console.warn('Failed to parse non-JSON error text as JSON', {
        status: response.status,
        contentType,
        parseError,
      });
    }
    return {
      rawError: {},
      fallbackText: textBody,
    };
  }
}

async function readErrorResponse(response: Response): Promise<ParsedErrorResponse> {
  const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';
  const isJsonResponse = contentType.includes('application/json');
  const { rawError: parsedRawError, fallbackText } = isJsonResponse
    ? {
        rawError: await parseJsonErrorResponse(response, contentType),
        fallbackText: '',
      }
    : await parseTextBodyErrorResponse(response, contentType);
  let rawError = parsedRawError;

  if (Object.keys(rawError).length === 0) {
    rawError = {
      detail: fallbackText || response.statusText || `Request failed (${response.status})`,
    };
  }

  return { rawError, fallbackText };
}

function normalizeApiError(
  response: Response,
  rawError: Record<string, unknown>,
  fallbackText: string
): NormalizedApiError {
  const errors = rawError.errors as Record<string, string[]> | undefined;
  const detail = rawError.detail;

  const detailString = asNonEmptyString(detail);
  if (detailString) {
    const isGenericServerText =
      response.status >= 500 && /^(internal server error|server error)$/i.test(detailString.trim());
    if (isGenericServerText || detailString.startsWith('<')) {
      return {
        code: 'UNKNOWN_ERROR',
        message: buildFallbackErrorMessage(response, fallbackText),
        errors,
      };
    }
    return {
      code: detailString,
      message: getMessageForErrorCode(detailString),
      errors,
    };
  }

  if (Array.isArray(detail)) {
    const firstError = detail[0] as { loc?: unknown[]; msg?: string; type?: string } | undefined;
    const field =
      firstError?.loc && Array.isArray(firstError.loc)
        ? firstError.loc.slice(1).join('.') || 'input'
        : 'input';
    return {
      code: firstError?.type || 'VALIDATION_ERROR',
      message: firstError?.msg || 'Validation error',
      errors,
      validationField: field,
    };
  }

  const directMessage =
    asNonEmptyString(rawError.message) ??
    asNonEmptyString(rawError.error) ??
    (isRecord(detail)
      ? (asNonEmptyString(detail.message) ??
        asNonEmptyString(detail.error) ??
        asNonEmptyString(detail.detail))
      : null);

  if (directMessage) {
    return {
      code: directMessage,
      message: getMessageForErrorCode(directMessage),
      errors,
    };
  }

  return {
    code: 'UNKNOWN_ERROR',
    message: buildFallbackErrorMessage(response, fallbackText),
    errors,
  };
}

function throwNormalizedApiError(statusCode: number, normalized: NormalizedApiError): never {
  if (normalized.validationField) {
    throw new ValidationError(normalized.validationField, normalized.message, normalized.errors);
  }

  if (statusCode === 401) {
    throw new AuthenticationError(normalized.message, normalized.code);
  }

  if ((statusCode === 422 || statusCode === 400) && normalized.errors) {
    const firstField = Object.keys(normalized.errors)[0];
    const firstError = normalized.errors[firstField]?.[0];
    throw new ValidationError(firstField, firstError || normalized.message, normalized.errors);
  }

  throw new ApiError(statusCode, normalized.message, normalized.errors, normalized.code);
}

/**
 * Main API request function with error handling and auth
 */
export async function apiRequest<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
  const {
    requiresAuth = true,
    skipAuthRefresh = false,
    skipAuthRecovery = false,
    headers = {},
    ...fetchOptions
  } = options;

  const url = endpoint.startsWith('http') ? endpoint : `${env.apiBaseUrl}${endpoint}`;

  // Build headers
  const requestHeaders: Record<string, string> = {
    ...(headers as Record<string, string>),
  };
  const hasExplicitAuthorizationHeader =
    Object.hasOwn(requestHeaders, 'Authorization') ||
    Object.hasOwn(requestHeaders, 'authorization');

  // Only add JSON Content-Type if not already set and not FormData
  if (!requestHeaders['Content-Type'] && !(fetchOptions.body instanceof FormData)) {
    requestHeaders['Content-Type'] = 'application/json';
  }

  // Add auth token if required
  if (requiresAuth && authToken) {
    requestHeaders['Authorization'] = `Bearer ${authToken}`;
  }
  // Ensure explicitly unauthenticated calls never leak auth headers, while preserving guest auth tokens.
  if (!requiresAuth && !hasExplicitAuthorizationHeader) {
    delete requestHeaders['Authorization'];
    delete requestHeaders['authorization'];
  }

  // Build request config
  const config: RequestInit = {
    ...fetchOptions,
    headers: requestHeaders,
    credentials: fetchOptions.credentials ?? (requiresAuth ? 'same-origin' : 'omit'),
  };

  try {
    // Make the request
    let response = await fetch(url, config);

    // Attempt a silent refresh on 401s before logging the user out
    if (
      response.status === 401 &&
      requiresAuth &&
      !skipAuthRefresh &&
      !skipAuthRecovery &&
      refreshToken
    ) {
      try {
        const newToken = await refreshAuthToken();
        const retryHeaders = new Headers(config.headers as HeadersInit | undefined);
        retryHeaders.set('Authorization', `Bearer ${newToken}`);

        response = await fetch(url, { ...config, headers: retryHeaders });
      } catch {
        // If refresh fails we fall back to the normal 401 handling below
      }
    }

    // Handle 401 - Unauthorized (token expired or invalid)
    if (response.status === 401 && requiresAuth && !skipAuthRecovery) {
      // Check if hydration has completed before logging out
      // If hydration hasn't completed, the 401 might be due to the race condition
      // where the API client doesn't have the token yet
      try {
        const { useAuthStore } = await import('../store/auth-store');
        const { hasHydrated, token } = useAuthStore.getState();

        if (!hasHydrated) {
          throw new AuthenticationError('Authentication pending - please wait');
        }

        if (!token) {
          throw new AuthenticationError('Not authenticated');
        }

        // Clear tokens from API client immediately
        clearTokens();
        useAuthStore.getState().logout();
      } catch {
        // If import fails, just clear tokens from API client
        clearTokens();
      }

      throw new AuthenticationError('Session expired. Please log in again.');
    }

    // Handle error responses
    if (!response.ok) {
      const { rawError, fallbackText } = await readErrorResponse(response);
      const normalized = normalizeApiError(response, rawError, fallbackText);
      throwNormalizedApiError(response.status, normalized);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    // Parse successful response
    const data = await response.json();

    // Handle wrapped responses (also considering if JSON is null)
    if (data && data.data !== undefined) {
      return data.data as T;
    }

    return data as T;
  } catch (error) {
    // Re-throw known error types, wrap unknown errors
    if (
      error instanceof ApiError ||
      error instanceof AuthenticationError ||
      error instanceof ValidationError
    ) {
      throw error;
    }
    throw error;
  }
}

// ============================================================================
// Convenience Methods
// ============================================================================

/**
 * GET request
 */
export async function get<T>(endpoint: string, options?: ApiRequestOptions): Promise<T> {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'GET',
  });
}

/**
 * POST request
 */
export async function post<T>(
  endpoint: string,
  data?: unknown,
  options?: ApiRequestOptions
): Promise<T> {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * PUT request
 */
export async function put<T>(
  endpoint: string,
  data?: unknown,
  options?: ApiRequestOptions
): Promise<T> {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * PATCH request
 */
export async function patch<T>(
  endpoint: string,
  data?: unknown,
  options?: ApiRequestOptions
): Promise<T> {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'PATCH',
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * DELETE request
 */
export async function del<T>(endpoint: string, options?: ApiRequestOptions): Promise<T> {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'DELETE',
  });
}

// ============================================================================
// Export default API client
// ============================================================================

export const api = {
  request: apiRequest,
  get,
  post,
  put,
  patch,
  delete: del,
  setAuthToken,
  getAuthToken,
  setRefreshToken,
  getRefreshToken,
  clearTokens,
  refreshAuthToken,
};

export default api;
